import { useMutation } from '@apollo/client'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { FormattedMessage } from '@faceup/localization'
import { Alert, Button } from '@faceup/ui-base'
import { CaseStatusGroup } from '@faceup/utils'
import { useContext } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { ChatPopupAlertType, PopupsContext } from './PopupsContext'
import { RelativeMessagesContext } from './RelativeMessagesContext'

const fragments = {
  ReportStatusAlert_case: graphql(`
    fragment ReportStatusAlert_case on CaseInterface {
      id
      status {
        id
        groupName
      }
      isHiddenFromStatistics
      isDemo
      channel {
        id
        sourceType
      }
      ... on CompanyReport {
        status {
          groupName
        }
      }
      organizationalUnit {
        id
        workingHours {
          id
          enabled
          outsideWorkingHoursMessage
        }
      }
      victim {
        id
      }
    }
  `),
}

const mutations = {
  ReopenCompanyReportMutation: graphql(`
    mutation ReopenCompanyReportMutation {
      reopenCompanyReport {
        case {
          id
          status {
            id
            groupName
          }
        }
      }
    }
  `),
}

type Props = {
  userId: string
  case: FragmentType<typeof fragments.ReportStatusAlert_case>
  isFirstMessage?: boolean
}

const ReportStatusAlert = ({ case: _case, userId, isFirstMessage }: Props) => {
  const aCase = getFragmentData(fragments.ReportStatusAlert_case, _case)
  const relativeMessages = useContext(RelativeMessagesContext)
  const { alertType, closeNotOpenReportAlert } = useContext(PopupsContext)
  const [reopenChat] = useMutation(mutations.ReopenCompanyReportMutation, {
    onCompleted: () => {
      closeNotOpenReportAlert()
    },
  })

  const userIsVictim = Boolean(aCase?.victim?.id) && userId === aCase?.victim?.id
  const isHiddenFromStatistics = aCase.isHiddenFromStatistics
  const isFollowUp = relativeMessages.type === 'Victim'

  if (alertType === ChatPopupAlertType.ReportStatus) {
    return (
      <div className='flex flex-col items-center'>
        <AlertMessage
          data-test='report-status-alert-report-closed'
          message={
            <FormattedMessage
              message={
                isHiddenFromStatistics
                  ? relativeMessages.archivedMessagesPerChannel[aCase.channel.sourceType]
                  : relativeMessages.closedMessagesPerChannel[aCase.channel.sourceType]
              }
            />
          }
          type={isHiddenFromStatistics ? 'info' : 'success'}
          statusGroupName={aCase.status.groupName}
          isFollowUp={isFollowUp}
        />
        {userIsVictim && (
          <div>
            <Button type='primary' onClick={() => reopenChat()}>
              {isFollowUp && <FormattedMessage message={relativeMessages.reopen} />}
            </Button>
          </div>
        )}
      </div>
    )
  }

  if (alertType === ChatPopupAlertType.OutsideWorkingHours && userIsVictim && isFirstMessage) {
    return (
      <AlertMessage
        data-test='report-status-alert-outside-working-hours'
        message={aCase.organizationalUnit.workingHours?.outsideWorkingHoursMessage ?? ''}
        type='warning'
        closable
        afterClose={() => closeNotOpenReportAlert()}
        statusGroupName={aCase.status.groupName}
        isFollowUp
      />
    )
  }

  return null
}

const AlertMessage = styled(Alert)<{
  isFollowUp?: boolean
  statusGroupName: CaseStatusGroup | null
}>`
  width: 100%;
  margin: 0 auto 20px;
  padding: 16px;
  border-radius: 6px;

  ${({ isFollowUp }) =>
    isFollowUp &&
    css`
      border: 0;
      font-weight: 500;
      line-height: 20px;
      font-size: 14px;
    `}

  background: ${({ statusGroupName }) =>
    statusGroupName === CaseStatusGroup.Closed
      ? '#0e9af714'
      : statusGroupName === CaseStatusGroup.Open
        ? '#0cce6b14'
        : '#f7990e14'};

  .ant-alert-message {
    color: ${({ statusGroupName }) =>
      statusGroupName === CaseStatusGroup.Closed
        ? '#0e9af7'
        : statusGroupName === CaseStatusGroup.Open
          ? '#0cce6b'
          : '#f7990e'};
  }

  button svg {
    color: ${({ statusGroupName }) =>
      statusGroupName === CaseStatusGroup.Closed
        ? '#0e9af7' // blue
        : statusGroupName === CaseStatusGroup.Open
          ? '#0cce6b' // green
          : '#f7990e'}; // orange
  }
`

export default ReportStatusAlert
